import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  LinearProgress,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Chip,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Tooltip,
  Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  PhotoLibrary as PhotoLibraryIcon,
  CompressOutlined as CompressIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Assessment as AssessmentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Speed as SpeedIcon,
  Storage as StorageIcon,
  TrendingDown as TrendingDownIcon,
  Refresh as RefreshIcon,
  CleaningServices as CleaningServicesIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import ImageMigrationService from '../../firebase/ImageMigrationService';

const ImageMigrationPanel = () => {
  // Estados principales
  const [stats, setStats] = useState(null);
  const [migrationProgress, setMigrationProgress] = useState(null);
  const [migrationResults, setMigrationResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMigrationRunning, setIsMigrationRunning] = useState(false);

  // Estados para configuración de migración
  const [migrationConfig, setMigrationConfig] = useState({
    batchSize: 3,
    deleteOriginals: false,
    createThumbnails: true,
    dryRun: false
  });

  // Estados para diálogos
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
    dangerous: false
  });

  // Cargar estadísticas al montar el componente
  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setIsLoading(true);
    try {
      const imageStats = await ImageMigrationService.getImageStats();
      setStats(imageStats);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartMigration = async () => {
    setConfirmDialog({
      open: true,
      title: '¿Confirmar migración de imágenes?',
      message: `Esta operación:
        
• Procesará ${(stats?.products.needsMigration || 0) + (stats?.categories.needsMigration || 0)} imágenes
• Reducirá el tamaño de archivo hasta un 80%
• Convertirá imágenes a formato WebP optimizado
• ${migrationConfig.createThumbnails ? 'Creará thumbnails automáticamente' : 'No creará thumbnails'}
• ${migrationConfig.deleteOriginals ? '⚠️ ELIMINARÁ las imágenes originales' : 'Mantendrá las imágenes originales como respaldo'}

${migrationConfig.dryRun ? '🧪 MODO PRUEBA: No se realizarán cambios reales' : ''}

La operación puede tardar varios minutos. ¿Continuar?`,
      onConfirm: executeImageMigration,
      dangerous: migrationConfig.deleteOriginals
    });
  };

  const executeImageMigration = async () => {
    setIsMigrationRunning(true);
    setMigrationProgress({ 
      current: 0, 
      total: (stats?.products.needsMigration || 0) + (stats?.categories.needsMigration || 0),
      stage: 'Iniciando...'
    });

    try {
      const results = await ImageMigrationService.migrateAllImages(migrationConfig);
      setMigrationResults(results);
      await loadStats(); // Recargar estadísticas
    } catch (error) {
      console.error('Error durante migración:', error);
      setMigrationResults({
        error: error.message
      });
    } finally {
      setIsMigrationRunning(false);
      setMigrationProgress(null);
    }
  };

  const handleCleanupBackups = async () => {
    setConfirmDialog({
      open: true,
      title: '⚠️ Limpiar respaldos de imágenes',
      message: 'Esta operación eliminará definitivamente las referencias a las imágenes originales de respaldo. Solo haz esto si estás completamente seguro de que las imágenes optimizadas funcionan correctamente.\n\n¿Estás seguro?',
      onConfirm: executeCleanup,
      dangerous: true
    });
  };

  const executeCleanup = async () => {
    setIsLoading(true);
    try {
      const results = await ImageMigrationService.cleanupBackupImages();
      console.log('Limpieza completada:', results);
      await loadStats();
    } catch (error) {
      console.error('Error durante limpieza:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const renderStats = () => {
    if (!stats) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    const totalImages = stats.products.total + stats.categories.total;
    const optimizedImages = stats.products.optimized + stats.categories.optimized;
    const needsMigration = stats.products.needsMigration + stats.categories.needsMigration;
    const optimizationPercentage = totalImages > 0 ? ((optimizedImages / totalImages) * 100).toFixed(1) : 0;

    return (
      <Grid container spacing={3}>
        {/* Tarjeta de resumen general */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AssessmentIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Estado General</Typography>
              </Box>
              <Typography variant="h4" color="primary" gutterBottom>
                {optimizationPercentage}% optimizado
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {optimizedImages} de {totalImages} imágenes procesadas
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={parseFloat(optimizationPercentage)} 
                sx={{ mt: 2, height: 8, borderRadius: 4 }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Tarjeta de acciones requeridas */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: needsMigration > 0 ? 'warning.50' : 'success.50' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {needsMigration > 0 ? (
                  <WarningIcon color="warning" sx={{ mr: 1 }} />
                ) : (
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                )}
                <Typography variant="h6">
                  {needsMigration > 0 ? 'Acción Requerida' : 'Todo Optimizado'}
                </Typography>
              </Box>
              <Typography variant="h4" color={needsMigration > 0 ? 'warning.main' : 'success.main'}>
                {needsMigration} imágenes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {needsMigration > 0 ? 'pendientes de optimización' : 'Todo está optimizado'}
              </Typography>
              {needsMigration > 0 && (
                <Button 
                  variant="contained" 
                  color="warning" 
                  sx={{ mt: 2 }}
                  onClick={handleStartMigration}
                  disabled={isMigrationRunning}
                  startIcon={<CompressIcon />}
                >
                  Optimizar Ahora
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Detalles por categoría */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Detalle por Tipo
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <PhotoLibraryIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6">Productos</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stats.products.optimized}/{stats.products.total} optimizados
                    </Typography>
                    {stats.products.needsMigration > 0 && (
                      <Chip 
                        label={`${stats.products.needsMigration} pendientes`}
                        color="warning" 
                        size="small" 
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <StorageIcon color="secondary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6">Categorías</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stats.categories.optimized}/{stats.categories.total} optimizadas
                    </Typography>
                    {stats.categories.needsMigration > 0 && (
                      <Chip 
                        label={`${stats.categories.needsMigration} pendientes`}
                        color="warning" 
                        size="small" 
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderMigrationProgress = () => {
    if (!migrationProgress && !isMigrationRunning) return null;

    const progress = migrationProgress ? 
      ((migrationProgress.current / migrationProgress.total) * 100) : 0;

    return (
      <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography variant="h6">
              Migración en Progreso
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {migrationProgress?.stage || 'Procesando...'}
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ mt: 2, height: 8, borderRadius: 4 }}
          />
          <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
            {migrationProgress?.current || 0} de {migrationProgress?.total || 0} completadas
          </Typography>
        </CardContent>
      </Card>
    );
  };

  const renderMigrationResults = () => {
    if (!migrationResults) return null;

    const { products, categories, totalSavings, error } = migrationResults;

    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error durante la migración</AlertTitle>
          {error}
        </Alert>
      );
    }

    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon color="success" sx={{ mr: 1 }} />
            Migración Completada
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
                <Typography variant="h4" color="success.main">
                  {(products?.success || 0) + (categories?.success || 0)}
                </Typography>
                <Typography variant="body2">Imágenes optimizadas</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.50' }}>
                <Typography variant="h4" color="info.main">
                  {Math.round(totalSavings || 0)} KB
                </Typography>
                <Typography variant="body2">Espacio ahorrado</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.50' }}>
                <Typography variant="h4" color="primary.main">
                  {((products?.errors?.length || 0) + (categories?.errors?.length || 0)) === 0 ? '100%' : 'Con errores'}
                </Typography>
                <Typography variant="body2">Tasa de éxito</Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Mostrar errores si existen */}
          {((products?.errors?.length || 0) + (categories?.errors?.length || 0)) > 0 && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography color="error">
                  Ver errores ({(products?.errors?.length || 0) + (categories?.errors?.length || 0)})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {products?.errors?.map((error, index) => (
                    <ListItem key={`product-error-${index}`}>
                      <ListItemIcon><WarningIcon color="error" /></ListItemIcon>
                      <ListItemText primary={error} />
                    </ListItem>
                  ))}
                  {categories?.errors?.map((error, index) => (
                    <ListItem key={`category-error-${index}`}>
                      <ListItemIcon><WarningIcon color="error" /></ListItemIcon>
                      <ListItemText primary={error} />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderConfiguration = () => {
    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <ScheduleIcon sx={{ mr: 1 }} />
            Configuración de Migración
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={migrationConfig.createThumbnails}
                    onChange={(e) => setMigrationConfig(prev => ({
                      ...prev,
                      createThumbnails: e.target.checked
                    }))}
                  />
                }
                label="Crear thumbnails automáticamente"
              />
              <Typography variant="caption" display="block" color="text.secondary">
                Genera versiones pequeñas para previsualizaciones rápidas
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={migrationConfig.dryRun}
                    onChange={(e) => setMigrationConfig(prev => ({
                      ...prev,
                      dryRun: e.target.checked
                    }))}
                  />
                }
                label="Modo prueba (no hacer cambios)"
              />
              <Typography variant="caption" display="block" color="text.secondary">
                Ejecuta sin modificar nada, solo para probar
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <FormControlLabel
                control={
                  <Switch 
                    checked={migrationConfig.deleteOriginals}
                    onChange={(e) => setMigrationConfig(prev => ({
                      ...prev,
                      deleteOriginals: e.target.checked
                    }))}
                    color="error"
                  />
                }
                label="⚠️ Eliminar imágenes originales"
              />
              <Typography variant="caption" display="block" color="error">
                CUIDADO: Esto eliminará permanentemente las imágenes originales. 
                Solo actívalo si estás completamente seguro.
              </Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Optimización de Imágenes
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gestiona la compresión y optimización de todas las imágenes en tu sistema
        </Typography>
      </Box>

      {renderMigrationProgress()}
      {renderMigrationResults()}

      {renderStats()}

      <Box sx={{ mt: 4 }}>
        {renderConfiguration()}
      </Box>

      <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button 
          variant="contained" 
          startIcon={<RefreshIcon />}
          onClick={loadStats}
          disabled={isLoading}
        >
          Actualizar Estadísticas
        </Button>

        {stats && (stats.products.optimized + stats.categories.optimized) > 0 && (
          <Tooltip title="Elimina referencias a imágenes de respaldo (solo si todo funciona bien)">
            <Button 
              variant="outlined"
              color="warning"
              startIcon={<CleaningServicesIcon />}
              onClick={handleCleanupBackups}
              disabled={isLoading}
            >
              Limpiar Respaldos
            </Button>
          </Tooltip>
        )}
      </Box>

      {/* Diálogo de confirmación */}
      <Dialog 
        open={confirmDialog.open} 
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          bgcolor: confirmDialog.dangerous ? 'error.50' : 'primary.50',
          display: 'flex',
          alignItems: 'center'
        }}>
          {confirmDialog.dangerous ? (
            <WarningIcon color="error" sx={{ mr: 1 }} />
          ) : (
            <CheckCircleIcon color="primary" sx={{ mr: 1 }} />
          )}
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ whiteSpace: 'pre-line', pt: 2 }}>
            {confirmDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>
            Cancelar
          </Button>
          <Button 
            variant="contained"
            color={confirmDialog.dangerous ? 'error' : 'primary'}
            onClick={() => {
              setConfirmDialog({ ...confirmDialog, open: false });
              confirmDialog.onConfirm?.();
            }}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImageMigrationPanel;